import React, { useMemo, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Drawer as MuiDrawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
  styled,
  Theme,
  CSSObject,
  CircularProgress,
  Button,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Assessment as AssessmentIcon,
  Storage as StorageIcon,
  DriveEta as DriveEtaIcon,
  Inventory as InventoryIcon,
  Insights as InsightsIcon,
  SmartToy as SmartToyIcon,
} from '@mui/icons-material';
import Divider from '@mui/material/Divider';

import { useReportGroups } from '../context/ReportGroupsContext';
import { useAuth } from '../context/AuthContext';

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(56px + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(56px + 1px)`,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})<{ open: boolean }>(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

interface MenuItem {
  name: string;
  path: string;
  icon: React.ReactElement;
}

export default function MainLayout() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const { reportGroups, loading } = useReportGroups();
  const { logout, username } = useAuth();

  const menuItems: MenuItem[] = useMemo(
    () =>
      reportGroups.map((group) => ({
        name: group.category,
        path: `/${group.id}`,
        icon: <AssessmentIcon />,
      })),
    [reportGroups],
  );

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <Drawer variant="permanent" open={open} PaperProps={{ sx: { backgroundColor: theme.palette.primary.dark } }}>
        <DrawerHeader sx={{ justifyContent: 'flex-start' }}>
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <AssessmentIcon style={{ width: 36, height: 36, color: 'white', marginLeft: 7 }} />
            <ListItemText
              primary="Conexio Support"
              slotProps={{
                primary: {
                  fontWeight: 500,
                  variant: 'body2',
                },
              }}
              sx={{
                color: 'white',
                opacity: open ? 1 : 0,
                fontWeight: 500,
                ml: 2,
              }}
            />
          </Box>
        </DrawerHeader>
        
        <List sx={{ flex: 1 }}>
          {loading && menuItems.length === 0 ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="100%"
              px={2}
            >
              <CircularProgress size={24} sx={{ color: 'white' }} />
            </Box>
          ) : (
            menuItems.map((menu) => (
              <ListItem
                key={menu.path}
                disablePadding
                sx={{
                  height: 56,
                  display: 'block',
                  backgroundColor: location.pathname.startsWith(menu.path)
                    ? theme.palette.primary.main
                    : '',
                  '&:hover': {
                    backgroundColor: location.pathname.startsWith(menu.path)
                      ? theme.palette.primary.main
                      : '#FFFFFF1A',
                  },
                }}
                onClick={() => navigate(menu.path)}
              >
                <ListItemButton
                  sx={{
                    minHeight: 48,
                    px: 2.5,
                    justifyContent: open ? 'initial' : 'center',
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      justifyContent: 'center',
                      color: '#fff',
                      mr: open ? 3 : 'auto',
                    }}
                  >
                    {menu.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={menu.name}
                    slotProps={{
                      primary: {
                        fontWeight: location.pathname.startsWith(menu.path) ? 500 : 400,
                        variant: 'body2',
                      },
                    }}
                    sx={{
                      color: 'white',
                      opacity: open ? 1 : 0,
                      fontWeight: location.pathname.startsWith(menu.path) ? 500 : 400,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))
          )}
          
          {/* Database Management Section */}
          <Divider sx={{ my: 1, backgroundColor: 'rgba(255,255,255,0.12)' }} />
          <ListItem
            disablePadding
            sx={{
              height: 56,
              display: 'block',
              backgroundColor: location.pathname === '/database'
                ? theme.palette.primary.main
                : '',
              '&:hover': {
                backgroundColor: location.pathname === '/database'
                  ? theme.palette.primary.main
                  : '#FFFFFF1A',
              },
            }}
            onClick={() => navigate('/database')}
          >
            <ListItemButton
              sx={{
                minHeight: 48,
                px: 2.5,
                justifyContent: open ? 'initial' : 'center',
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  justifyContent: 'center',
                  color: '#fff',
                  mr: open ? 3 : 'auto',
                }}
              >
                <StorageIcon />
              </ListItemIcon>
              <ListItemText
                primary="Database"
                slotProps={{
                  primary: {
                    fontWeight: location.pathname === '/database' ? 500 : 400,
                    variant: 'body2',
                  },
                }}
                sx={{
                  color: 'white',
                  opacity: open ? 1 : 0,
                  fontWeight: location.pathname === '/database' ? 500 : 400,
                }}
              />
            </ListItemButton>
          </ListItem>

          {/* Hertz Auto Section */}
          <ListItem
            disablePadding
            sx={{
              height: 56,
              display: 'block',
              backgroundColor: location.pathname === '/hertz'
                ? theme.palette.primary.main
                : '',
              '&:hover': {
                backgroundColor: location.pathname === '/hertz'
                  ? theme.palette.primary.main
                  : '#FFFFFF1A',
              },
            }}
            onClick={() => navigate('/hertz')}
          >
            <ListItemButton
              sx={{
                minHeight: 48,
                px: 2.5,
                justifyContent: open ? 'initial' : 'center',
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  justifyContent: 'center',
                  color: '#fff',
                  mr: open ? 3 : 'auto',
                }}
              >
                <DriveEtaIcon />
              </ListItemIcon>
              <ListItemText
                primary="Hertz Auto"
                slotProps={{
                  primary: {
                    fontWeight: location.pathname === '/hertz' ? 500 : 400,
                    variant: 'body2',
                  },
                }}
                sx={{
                  color: 'white',
                  opacity: open ? 1 : 0,
                  fontWeight: location.pathname === '/hertz' ? 500 : 400,
                }}
              />
            </ListItemButton>
          </ListItem>

          {/* VIN Inventory Section */}
          <ListItem
            disablePadding
            sx={{
              height: 56,
              display: 'block',
              backgroundColor: location.pathname === '/vin-inventory'
                ? theme.palette.primary.main
                : '',
              '&:hover': {
                backgroundColor: location.pathname === '/vin-inventory'
                  ? theme.palette.primary.main
                  : '#FFFFFF1A',
              },
            }}
            onClick={() => navigate('/vin-inventory')}
          >
            <ListItemButton
              sx={{
                minHeight: 48,
                px: 2.5,
                justifyContent: open ? 'initial' : 'center',
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  justifyContent: 'center',
                  color: '#fff',
                  mr: open ? 3 : 'auto',
                }}
              >
                <InventoryIcon />
              </ListItemIcon>
              <ListItemText
                primary="VIN Inventory"
                slotProps={{
                  primary: {
                    fontWeight: location.pathname === '/vin-inventory' ? 500 : 400,
                    variant: 'body2',
                  },
                }}
                sx={{
                  color: 'white',
                  opacity: open ? 1 : 0,
                  fontWeight: location.pathname === '/vin-inventory' ? 500 : 400,
                }}
              />
            </ListItemButton>
          </ListItem>

          {/* Fleet Intelligence Section */}
          <ListItem
            disablePadding
            sx={{
              height: 56,
              display: 'block',
              backgroundColor: location.pathname === '/fleet-intelligence'
                ? theme.palette.primary.main
                : '',
              '&:hover': {
                backgroundColor: location.pathname === '/fleet-intelligence'
                  ? theme.palette.primary.main
                  : '#FFFFFF1A',
              },
            }}
            onClick={() => navigate('/fleet-intelligence')}
          >
            <ListItemButton
              sx={{
                minHeight: 48,
                px: 2.5,
                justifyContent: open ? 'initial' : 'center',
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  justifyContent: 'center',
                  color: '#fff',
                  mr: open ? 3 : 'auto',
                }}
              >
                <InsightsIcon />
              </ListItemIcon>
              <ListItemText
                primary="Fleet Intelligence"
                slotProps={{
                  primary: {
                    fontWeight: location.pathname === '/fleet-intelligence' ? 500 : 400,
                    variant: 'body2',
                  },
                }}
                sx={{
                  color: 'white',
                  opacity: open ? 1 : 0,
                  fontWeight: location.pathname === '/fleet-intelligence' ? 500 : 400,
                }}
              />
            </ListItemButton>
          </ListItem>

          {/* Coco AI Assistant */}
          <Divider sx={{ my: 1, backgroundColor: 'rgba(255,255,255,0.12)' }} />
          <ListItem
            disablePadding
            sx={{
              height: 56,
              display: 'block',
              backgroundColor: location.pathname === '/coco'
                ? theme.palette.primary.main
                : '',
              '&:hover': {
                backgroundColor: location.pathname === '/coco'
                  ? theme.palette.primary.main
                  : '#FFFFFF1A',
              },
            }}
            onClick={() => navigate('/coco')}
          >
            <ListItemButton
              sx={{
                minHeight: 48,
                px: 2.5,
                justifyContent: open ? 'initial' : 'center',
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  justifyContent: 'center',
                  color: '#fff',
                  mr: open ? 3 : 'auto',
                }}
              >
                <SmartToyIcon />
              </ListItemIcon>
              <ListItemText
                primary="Coco AI"
                slotProps={{
                  primary: {
                    fontWeight: location.pathname === '/coco' ? 500 : 400,
                    variant: 'body2',
                  },
                }}
                sx={{
                  color: 'white',
                  opacity: open ? 1 : 0,
                  fontWeight: location.pathname === '/coco' ? 500 : 400,
                }}
              />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>

      <AppBar
        position="fixed"
        sx={{
          marginLeft: '56px',
          width: `calc(100% - ${open ? drawerWidth + 'px' : '56px'})`,
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar variant="dense">
          <Box
            onClick={handleDrawerToggle}
            sx={{
              width: 20,
              height: 36,
              borderBottomRightRadius: 8,
              borderTopRightRadius: 8,
              marginRight: 5,
              marginLeft: '-30px',
              display: 'flex',
              alignItems: 'center',
              backgroundColor: theme.palette.primary.dark,
              color: 'white',
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.8,
              },
            }}
          >
            {open ? <ChevronLeftIcon style={{ color: 'white' }} /> : <ChevronRightIcon style={{ color: 'white' }} />}
          </Box>
          <Typography variant="subtitle1" noWrap component="div">
            Reports Dashboard
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="outlined"
            size="small"
            onClick={handleLogout}
            sx={{
              color: 'white',
              borderColor: 'rgba(255,255,255,0.4)',
              textTransform: 'none',
              '&:hover': {
                borderColor: 'white',
                backgroundColor: 'rgba(255,255,255,0.08)',
              },
            }}
          >
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          padding: '24px 24px 24px 17px',
          paddingBottom: '0px',
          height: '100vh',
          width: `calc(100% - ${open ? drawerWidth + 'px' : '56px'})`,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <DrawerHeader style={{ minHeight: '40px', height: '40px' }} />
        <Box sx={{ flexGrow: 1 }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
